/**
 * useAnalytics Hook
 *
 * Custom hook providing access to analytics tracking functionality.
 * @module features/analytics/hooks/useAnalytics
 */
import { useMemo } from "react";

import { AnalyticsTracker } from "../utils/analyticsTracker";

/**
 * Custom hook providing access to analytics tracking functionality.
 *
 * Provides a stable reference to the AnalyticsTracker object for use
 * in React components. While the tracker itself is a singleton, this
 * hook ensures consistent patterns with other feature hooks and provides
 * a stable reference for dependency arrays.
 * @returns The AnalyticsTracker object with all logging methods
 * @example
 * ```typescript
 * import { useAnalytics } from '@/features/analytics';
 *
 * const MyComponent = () => {
 *   const analytics = useAnalytics();
 *
 *   useEffect(() => {
 *     analytics.logScreenView({
 *       screen_name: "MyComponent",
 *       screen_class: "MyComponent"
 *     });
 *   }, [analytics]);
 *
 *   const handleSignIn = () => {
 *     analytics.user.logUserSignIn({
 *       method: "phone",
 *       phone_number: "+1234567890",
 *       // ... other params
 *     });
 *   };
 *
 *   return <View>...</View>;
 * };
 * ```
 */
export function useAnalytics(): typeof AnalyticsTracker {
  return useMemo(() => AnalyticsTracker, []);
}
